import axios from 'axios';
import { canFill, loadCategories } from './surveyCategoriesService';

export const API_URL = `${process.env.REACT_APP_SURVEY_API_URL}/api`; // Reemplaza con la URL de tu API
export const API_TOKEN = process.env.REACT_APP_SURVEY_API_TOKEN; // Reemplaza con el token de tu API
export const API_URL_BASE = process.env.REACT_APP_SURVEY_API_URL; // Reemplaza con la URL de tu API

// POST directo al mismo endpoint que usa ModalAddSurveyResponse
// (newSurveyResponse interno) para crear una survey_response — extraído acá
// para poder enviar un borrador armado por ese modal (deferSubmit=true)
// desde un componente externo (ver Scanner/ScannerShell.js), sin duplicar
// la lógica de armado del payload (subida de archivos/firma, etc.) que sigue
// viviendo dentro del modal.
export const submitSurveyResponse = async (siteSlug, surveySlug, payload) => {
    const response = await axios.post(
        `${API_URL}/sites/${siteSlug}/surveys/${surveySlug}/webhook`,
        payload
    );
    return response.data;
};

export const makeAPIRequest = (url, method = 'get', params = {}, data = {}) => {
    const accessToken = localStorage.getItem('token');
  
    // Debug en desarrollo
    if (process.env.NODE_ENV === 'development') {
        console.log('🔑 Making API Request:', {
            url: `${API_URL}${url}`,
            method,
            hasToken: !!accessToken,
            params
        });
    }

    const headers = {
      headers: {
        Authorization: accessToken ? 'Bearer ' + accessToken : '',
      },
    };

    return axios({
        method,
        url: `${API_URL}${url}`,
        params,
        data,
        ...headers
    })
    .then((response) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('✅ API Response:', {
                url: `${API_URL}${url}`,
                status: response.status,
                dataLength: response.data?.data?.length || 'N/A',
                responseStructure: Object.keys(response.data || {}),
                fullResponse: response.data
            });
        }
        return response.data;
    })
    .catch((error) => {
        if (process.env.NODE_ENV === 'development') {
            console.error('❌ API Error:', {
                url: `${API_URL}${url}`,
                status: error.response?.status,
                message: error.response?.data?.message || error.message
            });
        }
        
        if (error.response?.status === 401) {
            console.warn('🔐 Token expired or invalid - consider refreshing authentication');
            // localStorage.removeItem('token');
        }
        throw error;
    });
}

// Filtrado de formularios para COMPLETAR según rol del usuario.
// Admin ve siempre todos. Otros roles se filtran por role_access.fill en la DB.
function filtrarFormulariosPorRol(formularios, rol) {
    if (rol === 'administrador') return formularios;
    return formularios.filter(formulario =>
        canFill(rol, formulario.category)
    );
}

// Nueva función para filtrar survey responses por etiquetas según rol de usuario
// NOTA: Por ahora solo filtra por lógica básica, cuando se implemente el backend
// con etiquetas, esta función se actualizará para usar response.tags
export const filterResponsesByRole = (responses, userRole) => {
    if (userRole === 'administrador') {
        return responses; // Administrador ve todas las respuestas
    }
    
    // Profesor: por ahora ve todas (pendiente implementar etiquetas en backend)
    // Cuando se agreguen etiquetas al backend, aquí se filtraría por:
    // - Excluir responses con tags: ['casos', 'reservado', 'medida-disciplinaria']
    // - Solo mostrar responses con privacy_level !== 'reservado'
    return responses;
};

// Función para obtener todas las respuestas de survey filtradas por rol
export const getSurveyResponsesByUser = async (userId, userRole) => {
    try {
        // Usar makeAPIRequest para incluir autenticación
        const response = await makeAPIRequest(`/survey-responses/user/${userId}`, 'get');
        
        // Aplicar filtrado por rol en frontend (temporal hasta backend con etiquetas)
        const filteredResponses = filterResponsesByRole(response.data || [], userRole);
        
        return {
            ...response,
            data: filteredResponses
        };
    } catch (error) {
        console.error('Error fetching survey responses by user:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Obtiene todas las respuestas de las encuestas.
 * @returns {Promise} La respuesta de la API con los datos de las respuestas de la encuesta.
 */
export const getSurveyResponses = async () => {
    const response = await axios.get(`${API_URL}/survey-responses`);
    return response.data;
};


/**
 * Obtiene los datos del encuestado.
 * @param {string} respondentId - El ID del encuestado.
 * @returns {Promise} La respuesta de la API con los datos del encuestado.
 */
export const getRespondent = async (respondentId) => {
    try {
        const response = await makeAPIRequest(`/respondents/${respondentId}`, 'get');
        return response;
    } catch (error) {
        console.error('Error fetching respondent:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Obtiene las encuestas del encuestado.
 * @param {string} respondentId - El ID del encuestado.
 * @returns {Promise} La respuesta de la API con los datos de las encuestas del encuestado.
 */
export const getRespondentSurveys = async (respondentId, { orderBy = 'created_at', orderDirection = 'desc', search = '' } = {}) => {
    try {
        // Construir los parámetros de consulta
        const params = {
            order_by: orderBy,
            order_direction: orderDirection
        };
        
        if (search) params.search = search;

        // Usar makeAPIRequest para incluir autenticación
        const response = await makeAPIRequest(`/respondents/${respondentId}/surveys`, 'get', params);
        return response;
    } catch (error) {
        console.error('Error al obtener encuestas del encuestado:', error);
        console.error('Error details:', error.response?.data || error.message);
        
        // En desarrollo, mostrar el error real
        if (process.env.NODE_ENV === 'development') {
            console.warn('Endpoint error - respondent may not exist or have no surveys:', error.response?.status);
        }
        
        // Devolver una estructura de datos vacía para mantener consistencia
        return {
            data: [],
            pagination: {
                current_page: 1,
                per_page: 10,
                total: 0,
                total_pages: 0
            }
        };
    }
};

/**
 * Obtiene las encuestas del encuestado filtradas por rol de usuario
 * @param {string} respondentId - El ID del encuestado.
 * @param {string} userRole - Rol del usuario autenticado
 * @returns {Promise} La respuesta de la API con los datos filtrados por rol
 */
export const getRespondentSurveysByRole = async (respondentId, userRole, options = {}) => {
    try {
        // Obtener las encuestas usando la función existente
        const response = await getRespondentSurveys(respondentId, options);
        
        // Aplicar filtrado por rol (por ahora solo lógica básica)
        const filteredResponses = filterResponsesByRole(response.data || [], userRole);
        
        return {
            ...response,
            data: filteredResponses
        };
    } catch (error) {
        console.error('Error al obtener encuestas filtradas por rol:', error);
        return {
            data: [],
            pagination: {
                current_page: 1,
                per_page: 10,
                total: 0,
                total_pages: 0
            }
        };
    }
};

/**
 * Obtiene todas las encuestas disponibles con cache inteligente.
 * @param {string} site_slug - El slug del sitio.
 * @param {Object} options - Opciones de filtrado y ordenamiento.
 * @param {Object} options.userAuth - (Opcional) Datos de usuario pre-obtenidos. Si se proporciona, evita llamada API redundante.
 * @param {boolean} options.forceRefresh - (Opcional) Fuerza actualización ignorando cache.
 * @returns {Promise} La respuesta de la API con los datos de todas las encuestas disponibles.
 */
export const getAllSurveys = async (site_slug, { orderBy = 'created_at', orderDirection = 'desc', search = '', groupBy = '', userAuth = null, forceRefresh = false } = {}) => {
    try {
        // Generar clave de cache
        const cacheKey = generateSurveysCacheKey(site_slug, { orderBy, orderDirection, search, groupBy });
        const now = Date.now();
        
        // Verificar cache válido
        const cached = surveysCache.get(cacheKey);
        const cacheIsValid = cached && 
                           cached.timestamp && 
                           (now - cached.timestamp < SURVEYS_CACHE_DURATION);

        if (!forceRefresh && cacheIsValid) {
            if (process.env.NODE_ENV === 'development') {
                console.log('📋 getAllSurveys: usando cache válido', { 
                    cacheKey,
                    cacheAge: Math.round((now - cached.timestamp) / 1000) + 's',
                    surveysCount: cached.data?.length || 0
                });
            }
            return cached.data;
        }

        // Hacer llamada API
        if (process.env.NODE_ENV === 'development') {
            console.log('🔄 getAllSurveys: haciendo llamada API', {
                cacheKey,
                reason: forceRefresh ? 'forced refresh' : 'cache expired/missing',
                site_slug
            });
        }

        const params = {
            order_by: orderBy,
            order_direction: orderDirection,
            search,
            group_by: groupBy
        };

        // Usar makeAPIRequest para mantener consistencia
        const response = await makeAPIRequest(`/sites/${site_slug}/surveys/`, 'get', params);

        // Asegurar que el caché de categorías esté poblado antes de filtrar
        await loadCategories();

        // Obtener rol del usuario: usar parámetro provisto o hacer llamada (con cache)
        let rolUsuarioAutenticado = 'administrador'; // Default fallback
        try {
            if (userAuth) {
                // Usar datos de usuario proporcionados (evita llamada API redundante)
                rolUsuarioAutenticado = userAuth.role || 'administrador';
                if (process.env.NODE_ENV === 'development') {
                    console.log('📋 getAllSurveys: usando userAuth proporcionado', {
                        role: rolUsuarioAutenticado,
                        user: userAuth.username
                    });
                }
            } else {
                // Hacer llamada pero usar cache si está disponible
                const user = await getUserAuth();
                rolUsuarioAutenticado = user?.role || 'administrador';
            }
        } catch (userError) {
            console.warn('⚠️ No se pudo obtener rol de usuario, usando administrador por defecto');
        }

        const formulariosFiltrados = filtrarFormulariosPorRol(response.data || response, rolUsuarioAutenticado);
        
        // Guardar en cache
        surveysCache.set(cacheKey, {
            data: formulariosFiltrados,
            timestamp: now
        });
        
        if (process.env.NODE_ENV === 'development') {
            console.log('✅ getAllSurveys: resultado cacheado', {
                cacheKey,
                surveysCount: formulariosFiltrados?.length || 0
            });
        }
        
        return formulariosFiltrados;
    } catch (error) {
        console.error('Error in getAllSurveys:', error);
        throw error;
    }
};

/**
 * Obtiene los datos de la encuesta.
 * @param {string} surveyId - El ID de la encuesta.
 * @returns {Promise} La respuesta de la API con los datos de la encuesta.
 */
export const getSurvey = async (surveyId) => {
    try {
        const response = await makeAPIRequest(`/surveys/${surveyId}`, 'get');
        return response;
    } catch (error) {
        console.error('Error fetching survey:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
}

/**
 * Ver SurveyResponse
 * @param {string} surveyResponseId - El ID de la respuesta de la encuesta.
 * @returns {Promise} La respuesta de la API con los datos de la respuesta de la encuesta.
 */
export const getSurveyResponse = async (surveyResponseId) => {
    try {
        const response = await makeAPIRequest(`/survey-responses/${surveyResponseId}`, 'get');
        return response;
    } catch (error) {
        console.error('Error fetching survey response:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
}

// Cache global para getUserAuth - evita múltiples llamadas API
let userAuthCache = null;
let userAuthCacheTimestamp = null;
const USER_AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos cache

// Promesa en vuelo - evita que llamadas concurrentes (varios componentes
// montando a la vez, antes de que exista cache) disparen múltiples GET /user.
let userAuthInFlight = null;

// Cache global para getAllSurveys - evita múltiples llamadas idénticas
let surveysCache = new Map();
const SURVEYS_CACHE_DURATION = 3 * 60 * 1000; // 3 minutos cache

export const getUserAuth = async (forceRefresh = false) => {
    // Verificar si tenemos cache válido y no se fuerza refresh
    const now = Date.now();
    const cacheIsValid = userAuthCache &&
                        userAuthCacheTimestamp &&
                        (now - userAuthCacheTimestamp < USER_AUTH_CACHE_DURATION);

    if (!forceRefresh && cacheIsValid) {
        if (process.env.NODE_ENV === 'development') {
            console.log('📋 getUserAuth: usando cache válido', {
                user: userAuthCache?.username,
                role: userAuthCache?.role,
                cacheAge: Math.round((now - userAuthCacheTimestamp) / 1000) + 's'
            });
        }
        return userAuthCache;
    }

    // Si ya hay una petición en curso, todos los llamadores concurrentes
    // esperan esa misma promesa en vez de disparar peticiones duplicadas.
    if (!forceRefresh && userAuthInFlight) {
        if (process.env.NODE_ENV === 'development') {
            console.log('⏳ getUserAuth: reutilizando petición en vuelo');
        }
        return userAuthInFlight;
    }

    // Hacer llamada API solo si no hay cache o está expirado
    if (process.env.NODE_ENV === 'development') {
        console.log('🔄 getUserAuth: haciendo llamada API', {
            reason: forceRefresh ? 'forced refresh' : 'cache expired/missing'
        });
    }

    userAuthInFlight = (async () => {
        try {
            const response = await makeAPIRequest('/user', 'get');
            const userData = response.user || response;

            // Actualizar cache
            userAuthCache = userData;
            userAuthCacheTimestamp = Date.now();

            if (process.env.NODE_ENV === 'development') {
                console.log('✅ getUserAuth response cached:', {
                    user: userData?.username,
                    role: userData?.role
                });
            }

            return userData;
        } catch (error) {
            console.error('❌ getUserAuth error:', error);
            console.error('Error details:', error.response?.data || error.message);

            // Limpiar cache en caso de error
            userAuthCache = null;
            userAuthCacheTimestamp = null;

            throw error;
        } finally {
            userAuthInFlight = null;
        }
    })();

    return userAuthInFlight;
}

// Función helper para limpiar cache manualmente
export const clearUserAuthCache = () => {
    userAuthCache = null;
    userAuthCacheTimestamp = null;
    if (process.env.NODE_ENV === 'development') {
        console.log('🗑️ getUserAuth cache cleared');
    }
};

// Función helper para limpiar cache de surveys
export const clearSurveysCache = () => {
    surveysCache.clear();
    if (process.env.NODE_ENV === 'development') {
        console.log('🗑️ getAllSurveys cache cleared');
    }
};

// Función helper para generar clave de cache de surveys
const generateSurveysCacheKey = (site_slug, options = {}) => {
    const { orderBy = 'created_at', orderDirection = 'desc', search = '', groupBy = '' } = options;
    return `${site_slug}:${orderBy}:${orderDirection}:${search}:${groupBy}`;
};

/**
 * Obtiene todas las respuestas de las encuestas.
 * @returns {Promise} La respuesta de la API con los datos de todas las respuestas de las encuestas.
 */
export const getAllSurveyResponses = async ({ 
    orderBy = 'created_at', 
    orderDirection = 'desc', 
    search = '', 
    startDate = '', 
    endDate = '' 
} = {}) => {
    const params = {
        order_by: orderBy,
        order_direction: orderDirection,
        search,
        start_date: startDate,
        end_date: endDate
    };

    const response = await axios.get(`${API_URL}/survey-responses`, { params });
    return response.data;
}

/**
 * Obtiene respuestas de encuestas filtradas por categoría (con auth).
 * Filtra por categoría en frontend como fallback si el backend no lo soporta.
 *
 * El backend pagina (per_page tope ~300 por request). La vista de Atención carga
 * todo el set en memoria para calcular stats/orden/filtros client-side, así que
 * acá se recorren todas las páginas del paginador y se concatenan antes de devolver,
 * en vez de devolver solo la primera página como "el total".
 */
export const getSurveyResponsesByCategory = async (category, options = {}) => {
    const { orderBy = 'created_at', orderDirection = 'desc', search = '', perPage = 300, questionText = '', questionValue = '' } = options;
    try {
        const baseParams = {
            order_by: orderBy,
            order_direction: orderDirection,
            search,
            per_page: perPage,
        };
        if (category) baseParams.category = category;
        if (questionText && questionValue) {
            baseParams.question_text = questionText;
            baseParams.question_value = questionValue;
        }

        const firstResponse = await makeAPIRequest('/survey-responses', 'get', { ...baseParams, page: 1 });
        const firstPaginator = firstResponse?.data;
        let records = firstPaginator?.data || (Array.isArray(firstPaginator) ? firstPaginator : []);
        const lastPage = firstPaginator?.last_page || 1;

        if (lastPage > 1) {
            const extraPages = await Promise.all(
                Array.from({ length: lastPage - 1 }, (_, i) => i + 2).map(page =>
                    makeAPIRequest('/survey-responses', 'get', { ...baseParams, page })
                )
            );
            extraPages.forEach(pageResponse => {
                const pagePaginator = pageResponse?.data;
                const pageRecords = pagePaginator?.data || (Array.isArray(pagePaginator) ? pagePaginator : []);
                records = records.concat(pageRecords);
            });
        }

        // Fallback client-side filter si el backend ignoró el param
        const filtered = category
            ? records.filter(r => r.survey?.category?.toLowerCase() === category.toLowerCase())
            : records;
        return {
            data: filtered,
            pagination: {
                total: firstPaginator?.total ?? filtered.length,
                perPage: firstPaginator?.per_page,
                currentPage: firstPaginator?.current_page,
                lastPage: firstPaginator?.last_page,
            },
        };
    } catch (error) {
        console.error(`Error fetching survey responses${category ? ` for category "${category}"` : ''}:`, error);
        return { data: [], pagination: null };
    }
};

/**
 * Obtiene los campos de opción (radio/select/checkbox) usados en los formularios de una
 * categoría, junto con los valores realmente respondidos — para armar el filtro "por pregunta"
 * (ej. Profesor, Categoría entrevista) con un desplegable de valores reales.
 * Retorna [{ text: 'Profesor', values: ['Juan Pérez', 'María Soto'] }, ...]
 */
export const getSurveyResponseFilterFields = async (category) => {
    try {
        const params = {};
        if (category) params.category = category;
        const response = await makeAPIRequest('/survey-responses/filter-fields', 'get', params);
        return Array.isArray(response) ? response : (response?.data || []);
    } catch (error) {
        console.error('Error fetching survey response filter fields:', error);
        return [];
    }
};

/**
 * Obtiene todos los archivos subidos a los perfiles de los encuestados.
 * @returns {Promise} La respuesta de la API con los datos de todos los archivos subidos a los perfiles de los encuestados.
 */
export const getAllFiles = async () => {
    const response = await axios.get(`${API_URL}/files`);
    return response.data;
}

  // Función auxiliar para obtener el rango de fechas
export const getDateRange = (rangeType) => {
    const endDate = new Date();
    let startDate;

    if (rangeType === 'last7days') {
      startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);
    } else if (rangeType === 'currentMonth') {
      startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

export const deleteSurvey = async (surveyId) => {
    if (confirm("¿Estás seguro de que desea eliminar el formulario?")) {
        try {
            const response = await makeAPIRequest(`/survey-responses/${surveyId}`, 'delete');
            console.log(response);
            return response; // Devuelve los datos si todo sale bien
        } catch (error) {
            console.error('Error eliminando la encuesta:', error);
            return false; // Retorna `false` si hay un error
        }
    } else {
        alert("Acción cancelada.");
        return false; // Retorna `false` si el usuario cancela
    }
}


export const getStatistics = async (params) => {
    const response = await axios.post(`${API_URL}/survey/stats`, params)
    return response
}

// ================================
// FUNCIONES PARA INTEGRACIÓN CON PRE-MATRÍCULAS
// ================================

/**
 * Busca la respuesta de survey para un usuario específico por slug de survey
 * @param {string} username RUT del usuario (ej: "12345678-9")
 * @param {string} surveySlug Slug del survey (ej: "pre-matriculas-2026-contrato")
 * @returns {Promise<Object|null>} Survey response o null si no existe
 */
export const getSurveyResponseByUserSlug = async (username, surveySlug) => {
    try {
        console.log(`[getSurveyResponseByUserSlug] Buscando survey: ${surveySlug} para usuario: ${username}`);
        
        // Usar el endpoint find-survey-response con parámetros
        const response = await makeAPIRequest(`/find-survey-response/${surveySlug}`, 'get', {
            respondent_id: username
        });

        if (response && Array.isArray(response) && response.length > 0) {
            console.log(`[getSurveyResponseByUserSlug] ✅ Encontrado survey response para ${username} en ${surveySlug}`);
            return response[0]; // Retornar el primero si hay múltiples
        }

        console.log(`[getSurveyResponseByUserSlug] ❌ No encontrado survey response para ${username} en ${surveySlug}`);
        return null;
    } catch (error) {
        console.error(`[getSurveyResponseByUserSlug] Error consultando survey ${surveySlug}:`, error);
        return null;
    }
};

/**
 * Mapea las respuestas del survey a formato de metadata de customer
 * @param {Object} surveyResponse Survey response completo con questions
 * @returns {Object} Metadata mapeado para customer
 */
export const mapSurveyToCustomerMeta = (surveyResponse) => {
    console.log(`[mapSurveyToCustomerMeta] Mapeando survey response ID: ${surveyResponse.surveyResponseId || surveyResponse.id}`);
    
    const metadata = {};
    
    if (!surveyResponse.questions || !Array.isArray(surveyResponse.questions)) {
        console.warn('[mapSurveyToCustomerMeta] No hay preguntas en el survey response');
        return metadata;
    }

    // Variables para concatenar nombres
    let apoderadoNombres = '';
    let apoderadoApellidoPaterno = '';
    let apoderadoApellidoMaterno = '';
    
    // Procesar cada pregunta
    surveyResponse.questions.forEach(question => {
        if (!question.response || !question.response.answer) {
            return; // Saltar preguntas sin respuesta
        }

        const answer = question.response.answer.trim();
        const sectionName = question.section?.name || '';
        const questionText = question.text || '';

        console.log(`[mapSurveyToCustomerMeta] Procesando: Sección="${sectionName}", Pregunta="${questionText}", Respuesta="${answer}"`);

        // MAPEO ESPECÍFICO BASADO EN SECCIÓN Y PREGUNTA
        if (sectionName === 'Datos personales del apoderado') {
            switch (question.id) {
                case 1: // Nombres
                    apoderadoNombres = answer;
                    break;
                case 2: // Apellido paterno
                    apoderadoApellidoPaterno = answer;
                    break;
                case 3: // Apellido materno
                    apoderadoApellidoMaterno = answer;
                    break;
                case 4: // RUT
                    metadata.apoderado_rut = answer;
                    break;
                case 5: // Curso
                    if (answer) {
                        metadata.group_name = answer;
                    }
                    break;
                case 6: // Domicilio
                    metadata.apoderado_direccion = answer;
                    break;
                case 7: // Ciudad
                    metadata.apoderado_comuna = answer;
                    break;
            }
        } else if (sectionName === 'Datos personales del alumno') {
            switch (question.id) {
                case 8: // Nombres del alumno
                    metadata.alumno_nombres = answer;
                    break;
                case 9: // Apellidos del alumno
                    metadata.alumno_apellidos = answer;
                    break;
                case 11: // RUT del alumno
                    metadata.alumno_rut = answer;
                    break;
            }
        }
    });

    // Construir nombre completo del apoderado
    const nombreCompleto = [apoderadoNombres, apoderadoApellidoPaterno, apoderadoApellidoMaterno]
        .filter(part => part && part.trim())
        .join(' ');
    
    if (nombreCompleto) {
        metadata.apoderado_nombre = nombreCompleto;
    }

    // Agregar email del respondent si está disponible
    if (surveyResponse.respondent && surveyResponse.respondent.email) {
        metadata.apoderado_email = surveyResponse.respondent.email;
    }

    // Agregar telefono si está en los datos del respondent (si existe en el modelo)
    if (surveyResponse.respondent && surveyResponse.respondent.telefono) {
        metadata.apoderado_telefono = surveyResponse.respondent.telefono;
    }

    console.log('[mapSurveyToCustomerMeta] ✅ Metadata mapeado:', metadata);
    return metadata;
};

/**
 * Obtiene datos actualizados de surveys para un usuario con lógica de fallback
 * @param {string} username RUT del usuario
 * @returns {Promise<Object|null>} Metadata mapeado o null si no encuentra datos
 */
export const getUpdatedCustomerDataFromSurveys = async (username) => {
    try {
        // Buscar todas las respuestas del usuario, ordenadas por más reciente primero
        const allResponses = await getRespondentSurveys(username, {
            orderBy: 'created_at',
            orderDirection: 'desc',
        });

        const responses = allResponses?.data || [];

        // Buscar la respuesta de matrícula más reciente con datos útiles
        for (const item of responses) {
            if (item.survey?.category !== 'matriculas') continue;
            if (!item.id) continue;

            try {
                const fullSurveyResponse = await getSurveyResponse(item.id);
                if (!fullSurveyResponse) continue;

                const metadata = mapSurveyToCustomerMeta(fullSurveyResponse);
                if (Object.keys(metadata).length === 0) continue;

                metadata._survey_source = item.survey?.slug || item.survey?.name;
                metadata._survey_response_id = item.id;
                metadata._updated_from_survey = new Date().toISOString();
                return metadata;
            } catch {
                continue;
            }
        }
    } catch (error) {
        console.error('[getUpdatedCustomerDataFromSurveys] Error:', error);
    }

    return null;
};
